import React, {
  useState,
  Children,
  useRef,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion, AnimatePresence } from "motion/react";

import "./Stepper.css";

// --- INUA CUSTOMIZATION ---
// This is the ReactBits "Stepper-JS-CSS" component (source fetched from
// https://reactbits.dev/r/Stepper-JS-CSS.json), ported to TypeScript with
// three additions on top of the original API:
//  - `accentColor` / `accentColorHover`: lets the caller theme the stepper
//    to match the seller's category color instead of the hardcoded purple
//    (#5227ff) the component ships with. These need to be real hex values,
//    not CSS custom properties (var(--t-500) etc.) — Framer Motion tweens
//    colors numerically when animating the active/complete step states, and
//    it can't resolve a CSS var() reference at animation time.
//  - `completeButtonText`: the original hardcodes the last step's button
//    label to the literal string "Complete" with no way to override it;
//    this makes it configurable (defaults to "Complete" to match original
//    behavior exactly when omitted).
//  - Imperative `goToStep` / `getCurrentStep` handle (via forwardRef): the
//    registry version only lets a step change originate from inside the
//    component (Back/Continue clicks or a step-indicator click). The seller
//    flow needs to jump the visual stepper to an arbitrary step from the
//    outside — e.g. Submit finds an empty required field that lives two
//    steps back and needs to open that step before focusing the field —
//    so a ref handle is exposed for that one case. Internal navigation
//    behavior is otherwise unchanged.
// Everything else — structure, animation logic, prop names — is unchanged
// from the registry source.

interface StepperProps {
  children?: React.ReactNode;
  initialStep?: number;
  onStepChange?: (step: number) => void;
  onFinalStepCompleted?: () => void;
  stepCircleContainerClassName?: string;
  stepContainerClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  backButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  nextButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  backButtonText?: string;
  nextButtonText?: string;
  completeButtonText?: string;
  disableStepIndicators?: boolean;
  renderStepIndicator?: (props: {
    step: number;
    currentStep: number;
    onStepClick: (clicked: number) => void;
  }) => React.ReactNode;
  accentColor?: string;
  accentColorHover?: string;
  [key: string]: any;
}

export interface StepperHandle {
  goToStep: (step: number) => void;
  getCurrentStep: () => number;
}

const Stepper = forwardRef<StepperHandle, StepperProps>(function Stepper(
  {
    children,
    initialStep = 1,
    onStepChange = () => {},
    onFinalStepCompleted = () => {},
    stepCircleContainerClassName = "",
    stepContainerClassName = "",
    contentClassName = "",
    footerClassName = "",
    backButtonProps = {},
    nextButtonProps = {},
    backButtonText = "Back",
    nextButtonText = "Continue",
    completeButtonText = "Complete",
    disableStepIndicators = false,
    renderStepIndicator,
    accentColor = "#5227FF",
    accentColorHover = "#5227FF",
    ...rest
  }: StepperProps,
  ref
) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState(0);
  const stepsArray = Children.toArray(children);
  const totalSteps = stepsArray.length;
  const isCompleted = currentStep > totalSteps;
  const isLastStep = currentStep === totalSteps;

  const updateStep = (newStep: number) => {
    setCurrentStep(newStep);
    if (newStep > totalSteps) {
      onFinalStepCompleted();
    } else {
      onStepChange(newStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setDirection(-1);
      updateStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (!isLastStep) {
      setDirection(1);
      updateStep(currentStep + 1);
    }
  };

  const handleComplete = () => {
    setDirection(1);
    updateStep(totalSteps + 1);
  };

  // Exposes a minimal imperative handle for the one case internal
  // navigation can't cover: jumping to an arbitrary step from outside.
  // Direction is inferred the same way internal clicks infer it, so the
  // slide animation still points the right way.
  useImperativeHandle(ref, () => ({
    goToStep: (step: number) => {
      setDirection(step > currentStep ? 1 : -1);
      updateStep(step);
    },
    getCurrentStep: () => currentStep,
  }));

  const [isNextHovered, setIsNextHovered] = useState(false);

  return (
    <div className="outer-container" {...rest}>
      <div
        className={`step-circle-container ${stepCircleContainerClassName}`}
        style={{ border: "1px solid #222" }}
      >
        <div className={`step-indicator-row ${stepContainerClassName}`}>
          {stepsArray.map((_, index) => {
            const stepNumber = index + 1;
            const isNotLastStep = index < totalSteps - 1;
            return (
              <React.Fragment key={stepNumber}>
                {renderStepIndicator ? (
                  renderStepIndicator({
                    step: stepNumber,
                    currentStep,
                    onStepClick: (clicked: number) => {
                      setDirection(clicked > currentStep ? 1 : -1);
                      updateStep(clicked);
                    },
                  })
                ) : (
                  <StepIndicator
                    step={stepNumber}
                    disableStepIndicators={disableStepIndicators}
                    currentStep={currentStep}
                    accentColor={accentColor}
                    onClickStep={(clicked: number) => {
                      setDirection(clicked > currentStep ? 1 : -1);
                      updateStep(clicked);
                    }}
                  />
                )}
                {isNotLastStep && (
                  <StepConnector
                    isComplete={currentStep > stepNumber}
                    accentColor={accentColor}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <StepContentWrapper
          isCompleted={isCompleted}
          currentStep={currentStep}
          direction={direction}
          className={`step-content-default ${contentClassName}`}
        >
          {stepsArray[currentStep - 1]}
        </StepContentWrapper>

        {!isCompleted && (
          <div className={`footer-container ${footerClassName}`}>
            <div
              className={`footer-nav ${currentStep !== 1 ? "spread" : "end"}`}
            >
              {currentStep !== 1 && (
                <button
                  onClick={handleBack}
                  className={`back-button ${
                    currentStep === 1 ? "inactive" : ""
                  }`}
                  type="button"
                  {...backButtonProps}
                >
                  {backButtonText}
                </button>
              )}
              <button
                onClick={isLastStep ? handleComplete : handleNext}
                className="next-button"
                type="button"
                onMouseEnter={() => setIsNextHovered(true)}
                onMouseLeave={() => setIsNextHovered(false)}
                style={{
                  backgroundColor: isNextHovered
                    ? accentColorHover
                    : accentColor,
                }}
                {...nextButtonProps}
              >
                {isLastStep ? completeButtonText : nextButtonText}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default Stepper;

function StepContentWrapper({
  isCompleted,
  currentStep,
  direction,
  children,
  className,
}: {
  isCompleted: boolean;
  currentStep: number;
  direction: number;
  children: React.ReactNode;
  className: string;
}) {
  const [parentHeight, setParentHeight] = useState(0);

  return (
    <motion.div
      className={className}
      style={{ position: "relative", overflow: "hidden" }}
      animate={{ height: isCompleted ? 0 : parentHeight }}
      transition={{ type: "spring", duration: 0.4 }}
    >
      <AnimatePresence initial={false} mode="sync" custom={direction}>
        {!isCompleted && (
          <SlideTransition
            key={currentStep}
            direction={direction}
            onHeightReady={(h) => setParentHeight(h)}
          >
            {children}
          </SlideTransition>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function SlideTransition({
  children,
  direction,
  onHeightReady,
}: {
  children: React.ReactNode;
  direction: number;
  onHeightReady: (height: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  // A single offsetHeight read right after mount (the previous behavior)
  // only reflects the *first* layout pass. If anything about the step's
  // real content settles a moment later — a web font swapping in, an
  // image finishing its load, a conditional field's own mount animation
  // completing — the true height changes, but nothing here was watching
  // for that, so `onHeightReady` never fires again and the panel is left
  // animated open to the too-small height from that first read. It only
  // ever got corrected by accident, whenever some unrelated re-render
  // (hovering the Continue button, typing into a field on another step)
  // happened to remount/rerun this effect. A ResizeObserver watches the
  // step's actual box for the rest of its life, so any later size change
  // — for any reason — re-measures and corrects the panel height itself,
  // without depending on the user coincidentally triggering a re-render.
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const measure = () => onHeightReady(node.offsetHeight);
    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children, onHeightReady]);

  return (
    <motion.div
      ref={containerRef}
      custom={direction}
      variants={stepVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.4 }}
      style={{ position: "absolute", left: 0, right: 0, top: 0 }}
    >
      {children}
    </motion.div>
  );
}

const stepVariants = {
  enter: (dir: number) => ({
    x: dir >= 0 ? "-100%" : "100%",
    opacity: 0,
  }),
  center: {
    x: "0%",
    opacity: 1,
  },
  exit: (dir: number) => ({
    x: dir >= 0 ? "50%" : "-50%",
    opacity: 0,
  }),
};

export function Step({ children }: { children: React.ReactNode }) {
  return <div className="step-default">{children}</div>;
}

function StepIndicator({
  step,
  currentStep,
  onClickStep,
  disableStepIndicators,
  accentColor,
}: {
  step: number;
  currentStep: number;
  onClickStep: (step: number) => void;
  disableStepIndicators: boolean;
  accentColor: string;
}) {
  const status =
    currentStep === step
      ? "active"
      : currentStep < step
      ? "inactive"
      : "complete";

  const handleClick = () => {
    if (step !== currentStep && !disableStepIndicators) onClickStep(step);
  };

  return (
    <motion.div
      onClick={handleClick}
      className="step-indicator"
      style={
        disableStepIndicators
          ? {
              pointerEvents: "none",
              // Only mute plain numbered (not-yet-reached) steps here —
              // dimming the whole indicator regardless of status made the
              // active/complete circles look duller than the connector
              // line right next to them, even though both use the same
              // accentColor. Inactive steps still get a visual "can't
              // click this" cue; active/complete stay at full brightness.
              opacity: status === "inactive" ? 0.5 : 1,
            }
          : {}
      }
      animate={status}
      initial={false}
    >
      <motion.div
        variants={{
          inactive: { scale: 1, backgroundColor: "#222", color: "#a3a3a3" },
          active: {
            scale: 1,
            backgroundColor: accentColor,
            color: accentColor,
          },
          complete: {
            scale: 1,
            backgroundColor: accentColor,
            color: accentColor,
          },
        }}
        transition={{ duration: 0.3 }}
        className="step-indicator-inner"
      >
        {status === "complete" ? (
          <CheckIcon className="check-icon" />
        ) : status === "active" ? (
          <div className="active-dot" />
        ) : (
          <span className="step-number">{step}</span>
        )}
      </motion.div>
    </motion.div>
  );
}

function StepConnector({
  isComplete,
  accentColor,
}: {
  isComplete: boolean;
  accentColor: string;
}) {
  // A width animation (0 -> 100%) can visually "snap" instead of running
  // smoothly, since the browser has to recompute layout on every frame of
  // the tween. scaleX animates as a GPU transform instead — no layout
  // recalculation, so the line reliably runs from left to right rather
  // than appearing to blink in already-filled. backgroundColor stays a
  // static style rather than something animated alongside scaleX — since
  // scaleX: 0 already fully hides the bar, tweening the color too would
  // just make the revealed portion look washed-out mid-fill instead of a
  // crisp solid line.
  const lineVariants = {
    incomplete: { scaleX: 0 },
    complete: { scaleX: 1 },
  };

  return (
    <div className="step-connector">
      <motion.div
        className="step-connector-inner"
        style={{
          width: "100%",
          transformOrigin: "left",
          backgroundColor: accentColor,
        }}
        variants={lineVariants}
        initial={false}
        animate={isComplete ? "complete" : "incomplete"}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      />
    </div>
  );
}

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          delay: 0.1,
          type: "tween",
          ease: "easeOut",
          duration: 0.3,
        }}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
